import React, { useEffect } from 'react';
import { Check } from 'lucide-react';
import confetti from 'canvas-confetti';

interface ConnectStravaStepProps {
  onConnect: () => void;
  onComplete: () => void;
  isConnecting?: boolean;
  isAlreadyConnected?: boolean;
}

const ConnectStravaStep: React.FC<ConnectStravaStepProps> = ({
  onConnect,
  onComplete,
  isConnecting = false,
  isAlreadyConnected = false
}) => {
  // Strava branding colors
  const STRAVA_ORANGE = '#FC4C02';

  // Trigger confetti on mount if connected
  useEffect(() => {
    if (isAlreadyConnected) {
      const duration = 3000;
      const end = Date.now() + duration;

      const frame = () => {
        // launch a few confetti from the left edge
        confetti({
          particleCount: 2,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#4f46e5', '#9333ea', '#22c55e'] // Indigo, Purple, Green
        });
        // and launch a few from the right edge
        confetti({
          particleCount: 2,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#4f46e5', '#9333ea', '#22c55e']
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      frame();

      // Also a big burst in the center
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#4f46e5', '#9333ea', '#22c55e']
      });
    }
  }, [isAlreadyConnected]);

  // If user is already connected, show success state with option to proceed
  if (isAlreadyConnected) {
    return (
      <div className="h-full flex flex-col text-center animate-fade-in relative">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-2 custom-scrollbar pb-24 md:pb-0 md:h-full md:grid md:grid-cols-2 md:gap-12 md:px-8 md:items-center">

          {/* Left Column: Text Info */}
          <div className="md:text-left space-y-4 md:space-y-6 animate-slide-up [animation-delay:100ms] mt-6 md:mt-0">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800">
              Đã kết nối Strava!
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto md:mx-0 text-lg md:text-xl leading-relaxed">
              Tài khoản Strava của bạn đã được liên kết trước đó. Bạn có thể tiếp tục sử dụng ứng dụng.
            </p>
            <div className="bg-green-50/60 backdrop-blur-md p-6 rounded-3xl border border-green-100/50 max-w-xl mx-auto md:mx-0 text-left text-base text-green-900 shadow-sm animate-slide-up [animation-delay:200ms] mt-8">
              <strong>Tin vui:</strong> Bạn không cần phải kết nối lại. Chỉ cần nhấn nút bên dưới để tiếp tục!
            </div>
          </div>

          {/* Right Column: Visual & Action */}
          <div className="flex flex-col items-center justify-center space-y-8 md:bg-slate-50 md:p-8 md:rounded-3xl md:h-auto md:py-12">
            <div className="relative w-20 h-20 sm:w-32 sm:h-32 mx-auto">
              <div className="absolute inset-0 bg-green-500/20 rounded-full blur-2xl animate-pulse-slow"></div>
              <div className="relative w-20 h-20 sm:w-32 sm:h-32 bg-green-50/80 backdrop-blur-md rounded-3xl flex items-center justify-center border border-green-200 shadow-lg shadow-green-500/20 animate-scale-bounce">
                <Check className="w-10 h-10 sm:w-16 sm:h-16 text-green-600" strokeWidth={2.5} />
              </div>
            </div>

            {/* Desktop Button */}
            <div className="hidden md:block w-full max-w-xs animate-slide-up [animation-delay:300ms]">
              <button
                onClick={onComplete}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-2xl transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-500/20 hover:shadow-indigo-500/30 hover:-translate-y-1 active:scale-[0.98] text-lg"
              >
                Vào Dashboard
              </button>
            </div>
          </div>
        </div>

        <div className="flex-none p-4 w-full bg-white/80 backdrop-blur-md border-t border-slate-100 md:hidden z-10 animate-slide-up [animation-delay:300ms]">
          <button
            onClick={onComplete}
            className="w-full max-w-md mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-5 px-8 rounded-2xl transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-500/20 hover:shadow-indigo-500/30 hover:-translate-y-1 active:scale-[0.98] text-lg"
          >
            Vào Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Default: Show connect button
  return (
    <div className="h-full flex flex-col text-center animate-fade-in relative">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 py-2 custom-scrollbar pb-24 md:pb-0 md:h-full flex flex-col md:grid md:grid-cols-2 md:gap-12 md:px-8 md:items-center">

        {/* Left Column: Info (Order 2 on Mobile) */}
        <div className="order-2 md:order-none md:text-left space-y-4 md:space-y-6 mt-6 md:mt-0">
          <h2 className="text-3xl md:text-5xl font-bold text-slate-800">
            Kết nối Strava
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto md:mx-0 text-lg md:text-xl leading-relaxed">
            Sử dụng tài khoản Strava để đồng bộ hoạt động chạy bộ, đạp xe và bơi lội của bạn.
          </p>
          <div className="bg-orange-50/60 backdrop-blur-md p-6 rounded-3xl border border-orange-100/50 max-w-xl mx-auto md:mx-0 text-left text-base text-orange-900 shadow-sm user-select-none mt-8">
            <strong>Lưu ý quan trọng:</strong> Khi kết nối, vui lòng cấp đủ quyền truy cập được yêu cầu để hệ thống có thể đọc dữ liệu.
          </div>
        </div>

        {/* Right Column: Visual & Action (Order 1 on Mobile) */}
        <div className="order-1 md:order-none flex flex-col items-center justify-center space-y-8 md:bg-orange-50/30 md:p-8 md:rounded-3xl md:h-auto md:py-12 md:border md:border-orange-100 mt-6 md:mt-0">
          <div className="relative w-20 h-20 sm:w-32 sm:h-32 mx-auto">
            <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl animate-pulse-slow"></div>
            <div className="relative w-20 h-20 sm:w-32 sm:h-32 bg-orange-50/80 backdrop-blur-md rounded-3xl flex items-center justify-center border border-orange-100 shadow-lg shadow-orange-500/20">
              <img
                src="https://seeklogo.com/images/S/strava-logo-C419D1A461-seeklogo.com.png"
                alt="Strava"
                className="w-10 h-10 sm:w-16 sm:h-16 object-contain"
                onError={(e) => {
                  // Fallback in case image fails
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement?.classList.add('text-orange-600');
                  e.currentTarget.parentElement!.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>';
                }}
              />
            </div>
          </div>

          {/* Desktop Button */}
          <div className="hidden md:block w-full max-w-xs">
            <button
              onClick={onConnect}
              disabled={isConnecting}
              className="w-full text-white font-bold py-4 px-8 rounded-2xl transition-all flex items-center justify-center gap-2 shadow-xl shadow-orange-500/20 hover:shadow-orange-500/30 hover:-translate-y-1 active:scale-[0.98] text-lg"
              style={{ backgroundColor: STRAVA_ORANGE }}
            >
              {isConnecting ? (
                <>
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Đang kết nối...
                </>
              ) : (
                "Kết nối với Strava"
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-none p-4 w-full bg-white/80 backdrop-blur-md border-t border-slate-100 md:hidden z-10">
        <button
          onClick={onConnect}
          disabled={isConnecting}
          className="w-full max-w-md mx-auto text-white font-bold py-5 px-8 rounded-2xl transition-all flex items-center justify-center gap-2 shadow-xl shadow-orange-500/20 hover:shadow-orange-500/30 hover:-translate-y-1 active:scale-[0.98] text-lg"
          style={{ backgroundColor: STRAVA_ORANGE }}
        >
          {isConnecting ? (
            <>
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Đang kết nối...
            </>
          ) : (
            "Kết nối với Strava"
          )}
        </button>
      </div>
    </div>
  );
};

export default ConnectStravaStep;
